import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import TeamLinkEditModal from "@/pages/employees/team/widgets/TeamLinkEditModal.tsx";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import { useTeamSave } from "@/queries/use-teams.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { EmployeeFilterDefault } from "@/types/employees/employee.types.ts";
import {
  ApiTeam,
  ApiTeamLink,
  ApiTeamMember,
  ApiTeamSavePayload,
  getTeamEmoji,
  TeamMemberRoleItems,
  TeamMemberRoleType,
} from "@/types/team.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Box,
  Button,
  DropdownSelect,
  FontAwesomeIcon,
  Input,
  LinkTo,
  Modal,
  ModalProps,
  Select,
  SelectValueType,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { isEqual } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

interface TeamEditModalProps extends ModalProps {
  team: ApiTeam | undefined;
}

export default function TeamEditModal({ team, ...props }: TeamEditModalProps) {
  const { t } = useTranslation();
  const modalsService = useModals();
  const { close } = useModals();
  const { id } = props;
  const navigate = useNavigate();

  const teamSchema = z.object({
    emoji: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    description: z.string().nullable().optional(),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(teamSchema),
    values: team
      ? {
          title: team.title,
          emoji: getTeamEmoji(team),
          description: team.description,
        }
      : undefined,
    defaultValues: {
      title: "",
      description: "",
      emoji: getTeamEmoji(team),
    },
  });

  const [members, setMembers] = useState<ApiTeamMember[]>(team?.members ?? []);
  const [links, setLinks] = useState<ApiTeamLink[]>(team?.links ?? []);

  const employeesSearch = useEmployeeSearchInline(true, {
    ...EmployeeFilterDefault,
    exclude_ids: members.map((x) => x.employee.id),
  });

  useEffect(() => {
    const currentIds = members.map((x) => x.employee.id).sort();
    const prevIds = (employeesSearch.filter.exclude_ids ?? []).sort();

    if (!isEqual(currentIds, prevIds)) {
      employeesSearch.setFilter({
        ...employeesSearch.filter,
        exclude_ids: currentIds,
      });
    }
  }, [members]);

  const handleLinkModal = useCallback(
    (link?: ApiTeamLink, index?: number) => {
      modalsService.openModal(TeamLinkEditModal, {
        link: link,
        onSubmit: (newLink: ApiTeamLink) => {
          setLinks((prev) => {
            if (index !== undefined) {
              return prev.map((l, i) => (i === index ? { ...l, ...newLink } : l));
            } else {
              return [...prev, newLink];
            }
          });
        },
        onDelete: () => {
          if (index !== undefined) {
            setLinks((prev) => prev.filter((_, i) => i !== index));
          }
        },
      });
    },
    [modalsService]
  );

  const linksOnDragEnd = useCallback((result: DropResult) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;

    setLinks((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(source.index, 1);
      updated.splice(destination.index, 0, moved);
      return updated;
    });
  }, []);

  const membersOnDragEnd = useCallback((result: DropResult) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;

    setMembers((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(source.index, 1);
      updated.splice(destination.index, 0, moved);
      return updated;
    });
  }, []);

  const handleMemberAdd = useCallback(
    (item: SelectValueType) => {
      const addMember = employeesSearch.data?.items.find((x) => x.id == item);
      if (!addMember) return;
      setMembers((prev) => [
        ...prev,
        {
          employee: addMember,
          role: TeamMemberRoleType.member,
          id: 0,
          sort: 0,
        },
      ]);
    },
    [employeesSearch.data]
  );
  const handleMemberRoleChange = useCallback((index: number, newRole: TeamMemberRoleType) => {
    setMembers((prevMembers) => {
      const updatedMembers = [...prevMembers];
      updatedMembers[index] = {
        ...updatedMembers[index],
        role: newRole,
      };
      return updatedMembers;
    });
  }, []);
  const handleMemberDelete = useCallback((index: number) => {
    setMembers((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const teamSave = useTeamSave(control);
  const onSubmit = handleSubmit(async (teamSchema) => {
    const teamPayload: ApiTeamSavePayload = {
      title: teamSchema.title,
      emoji: teamSchema.emoji,
      description: teamSchema.description,
      links: links.map((x) => ({
        title: x.title,
        url: x.url,
      })),
      members: members.map((x) => ({
        employee_id: x.employee.id,
        role: x.role,
        description: x.description,
      })),
    };

    try {
      const data = await teamSave.mutateAsync({
        id: team?.id,
        payload: teamPayload,
      });

      if (team?.id) {
        analyticsService.trackEvent(analyticEvents.team.teamEdited, {
          [analyticProperties.id]: data.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.team.teamCreated, {
          [analyticProperties.id]: data.id,
        });
      }

      close(id);
      navigate(`/employees/teams/${data.id}`);
    } catch {
      return;
    }
  });

  return (
    <>
      <Modal
        {...props}
        title={team?.id ? t("core:edit_team") : t("core:new_team")}
        withCloser
        size={"lg"}
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button type={"submit"} size={"lg"} isLoading={teamSave.isPending} onClick={onSubmit}>
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={onSubmit}>
          <Stack className={"pb-8"} items={"center"} gap={"2xl"}>
            <Controller
              control={control}
              render={({ field: { value, onChange } }) => (
                <EmojiPickerDropdown
                  onEmojiSelect={(emoji) => {
                    onChange(emoji);
                  }}
                >
                  <Box className={"relative w-41 h-41 group cursor-pointer shadow-sm rounded-lg"}>
                    <Avatar variant={"opaque"} url={undefined} emoji={value} size={"2xl"} />
                    <Box
                      className={
                        "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-lg flex items-center justify-center"
                      }
                    >
                      <FontAwesomeIcon
                        icon={"fa-light fa-icons"}
                        className={"text-[74px] text-light "}
                      />
                    </Box>
                  </Box>
                </EmojiPickerDropdown>
              )}
              name={"emoji"}
            />
          </Stack>

          <Input
            required={true}
            label={t("common:title")}
            autoFocus={true}
            type="text"
            {...register("title")}
            error={errors.title?.message}
          />

          <Title header={t("common:description")} size={"xs"} paddingTop />
          <Controller
            render={({ field: { value, onChange } }) => (
              <RichEditor
                value={value || ""}
                onChange={onChange}
                error={errors.description?.message}
                placeholder={t("common:description_enter_using_editor")}
              />
            )}
            control={control}
            name={"description"}
          />

          <Title size={"xs"} header={t("core:links")} paddingTop />
          <Stack className={"pt-4"} gap={"sm"}>
            <DragDropContext onDragEnd={linksOnDragEnd}>
              <Droppable droppableId="links">
                {(provided) => (
                  <Box
                    className={"flex flex-col gap-3"}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {links.map((link, index) => (
                      <Draggable
                        key={`key-link-item-${index}`}
                        draggableId={`draggable-link-item-${index}`}
                        index={index}
                      >
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{ ...provided.draggableProps.style }}
                            className={"items-baseline"}
                          >
                            <Stack direction={"horizontal"} items={"center"} gap={"smd"}>
                              <Box {...provided.dragHandleProps}>
                                <FontAwesomeIcon
                                  className={"m-2"}
                                  icon="fa-light fa-grip-dots-vertical"
                                />
                              </Box>
                              <LinkTo to={link.url} target={"_blank"} variant={"secondary"}>
                                <Text>{link.title}</Text>
                              </LinkTo>
                              <Stack direction={"horizontal"} gap={"smd"} className={"ml-auto"}>
                                <Button
                                  variant={"plain"}
                                  leftIcon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
                                  onClick={() => handleLinkModal(link, index)}
                                />
                              </Stack>
                            </Stack>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
            <Button
              size="sm"
              variant={"secondary"}
              onClick={() => handleLinkModal()}
              type={"button"}
            >
              {t("core:add_link")}
            </Button>
          </Stack>

          <Title size={"xs"} header={t("core:members")} paddingTop />
          <Select
            label={t("core:add_members")}
            emptyTitle={t("common:select")}
            value={undefined}
            showAvatar={true}
            showCaption={true}
            searchable
            loadOptionsOnOpen
            onSearchOptions={(text) => employeesSearch.search(text)}
            searchOptions={employeesSearch.options}
            onChange={(item) => handleMemberAdd(item)}
          />
          <Box className={"pt-4"}>
            <DragDropContext onDragEnd={membersOnDragEnd}>
              <Droppable droppableId="members">
                {(provided) => (
                  <Box
                    className={"flex flex-col"}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {members.map((member, index) => (
                      <Draggable
                        key={`key-member-item-${index}-${member.employee.id}-${member.id}`}
                        draggableId={`draggable-member-item-${index}-${member.employee.id}-${member.id}`}
                        index={index}
                      >
                        {(provided) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{ ...provided.draggableProps.style }}
                          >
                            <Stack direction={"horizontal"} items={"center"} gap={"smd"}>
                              <Box {...provided.dragHandleProps}>
                                <FontAwesomeIcon
                                  className={"m-2"}
                                  icon="fa-light fa-grip-dots-vertical"
                                />
                              </Box>
                              <EmployeeListItem
                                employee={member.employee}
                                valueSlot={
                                  <Stack
                                    direction={"horizontal"}
                                    gap={"smd"}
                                    items={"center"}
                                    className={"ml-auto"}
                                  >
                                    <DropdownSelect
                                      value={member.role}
                                      options={TeamMemberRoleItems()}
                                      showCaption={false}
                                      onChange={(newRole) =>
                                        handleMemberRoleChange(index, newRole as TeamMemberRoleType)
                                      }
                                    />
                                    <Button
                                      variant={"plain"}
                                      leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                                      onClick={() => handleMemberDelete(index)}
                                    />
                                  </Stack>
                                }
                              />
                            </Stack>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </form>
      </Modal>
    </>
  );
}
